import * as AspectRatio from '@radix-ui/react-aspect-ratio';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  BreakpointFlags,
  ImagesSourceType,
  appendWebpFormat,
  documentSupportsSrcSet,
  getFallbackSrcWhenSrcSetUnsupported,
  sizesFromBreakpoints,
  sourcesFromBreakpoints,
  srcSetFromWidths
} from '@/lib/helpers/images';
import { BreakpointObjectType, ScreenSizeQueries } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useInViewport from '@/hooks/useInViewport';
import { useImageSizeObserver } from '@/hooks/useImageSizeObserver';

export interface ResponsiveImageProps {
  alt?: string;
  aspectRatio?: number;
  className?: string;
  imageSizes?: BreakpointObjectType;
  imageSources?: ImagesSourceType;
  onLoad?: () => void;
  src?: string;
  propertyName?: string;
  loading?: 'lazy' | 'eager';
  title?: string;
  skipWrapper?: boolean;
}

const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  alt,
  aspectRatio = 1,
  className,
  imageSizes,
  imageSources,
  onLoad,
  src,
  loading,
  title,
  skipWrapper
}) => {
  const xs = useMediaQuery(ScreenSizeQueries.xs);
  const sm = useMediaQuery(ScreenSizeQueries.sm);
  const md = useMediaQuery(ScreenSizeQueries.md);
  const lg = useMediaQuery(ScreenSizeQueries.lg);
  const xl = useMediaQuery(ScreenSizeQueries.xl);
  const xxl = useMediaQuery(ScreenSizeQueries['2xl']);

  const [imageRef, isImageInView] = useInViewport<HTMLImageElement>();
  const { width, height } = useImageSizeObserver([imageRef]);

  const breakPoints: BreakpointFlags = useMemo(
    () => ({
      xs,
      sm,
      md,
      lg,
      xl,
      xxl
    }),
    [xs, sm, md, lg, xl, xxl]
  );

  const supportsSrcSet = useMemo(() => documentSupportsSrcSet(), []);

  // Compute everything up front to keep Hooks valid
  const srcSet = useMemo(
    () => (src ? srcSetFromWidths(src, { ratio: aspectRatio }) : ''),
    [src, aspectRatio]
  );
  const sizes = useMemo(() => sizesFromBreakpoints(imageSizes), [imageSizes]);
  const sources = useMemo(() => sourcesFromBreakpoints(imageSources), [imageSources]);

  const fallbackSrc = useMemo(() => {
    if (!src) return '';
    return getFallbackSrcWhenSrcSetUnsupported(src, aspectRatio, imageSizes, breakPoints);
  }, [src, aspectRatio, imageSizes, breakPoints]);

  const imageSrc = useMemo(() => {
    return supportsSrcSet ? appendWebpFormat(src) : fallbackSrc;
  }, [fallbackSrc, src, supportsSrcSet]);

  const imgLoading = useMemo(
    () => loading ?? (isImageInView ? 'eager' : 'lazy'),
    [isImageInView, loading]
  );
  const fetchPriority = useMemo(
    () => (imgLoading === 'eager' ? 'high' : isImageInView ? 'auto' : 'low'),
    [isImageInView, imgLoading]
  );

  if (!src) return null;

  const Wrapper = skipWrapper ? React.Fragment : AspectRatio.Root;

  return (
    <Wrapper {...(!skipWrapper ? { ratio: aspectRatio } : {})}>
      {supportsSrcSet ? (
        <picture>
          {sources?.map(source => (
            <source
              key={source.breakpoint}
              srcSet={source.srcSet}
              media={source.media}
              sizes={sizes}
            />
          ))}
          <img
            ref={imageRef}
            className={twMerge(classnames('size-full max-w-full object-cover', className))}
            alt={alt}
            onLoad={onLoad}
            src={imageSrc}
            srcSet={srcSet}
            sizes={sizes}
            loading={imgLoading}
            fetchPriority={fetchPriority}
            title={title}
            width={width}
            height={height}
          />
        </picture>
      ) : (
        <img
          ref={imageRef}
          className={twMerge(classnames('size-full object-cover', className))}
          alt={alt}
          src={imageSrc}
          title={title}
          onLoad={onLoad}
          loading={imgLoading}
          fetchPriority={fetchPriority}
          width={width}
          height={height}
        />
      )}
    </Wrapper>
  );
};

export default ResponsiveImage;
